import { AxiosError } from "axios";
import {
  addBreadcrumb as sentryAddBreadcrumb,
  captureException,
  captureMessage,
  withScope,
} from "@sentry/react";

type SafePrimitive = string | number | boolean | null | undefined;
export type SafeContext = Record<string, SafePrimitive>;

type CaptureAppErrorOptions = {
  area: string;
  action: string;
  tags?: SafeContext;
  extra?: SafeContext;
};

type AppBreadcrumbOptions = {
  category: string;
  message: string;
  level?: "debug" | "info" | "warning" | "error";
  data?: SafeContext;
};

type ScrubbableSentryEvent = {
  request?: unknown;
  extra?: unknown;
  contexts?: unknown;
  breadcrumbs?: unknown;
};

const SENSITIVE_KEY_PATTERN =
  /authorization|password|token|secret|cookie|credential|email|phone|contact|address|postal|floor|unit|notes|otp|card|headers|booking_data|promo_code|name/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function compactSafeContext(context?: SafeContext): Record<string, string | number | boolean | null> {
  const safe: Record<string, string | number | boolean | null> = {};

  for (const [key, value] of Object.entries(context ?? {})) {
    if (value === undefined) continue;
    safe[key] = value;
  }

  return safe;
}

function sanitizeValue(value: unknown, depth = 0): unknown {
  if (depth > 3) return "[Filtered]";
  if (value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeValue(item, depth + 1));
  }

  if (!isRecord(value)) return "[Filtered]";

  const sanitized: Record<string, unknown> = {};
  for (const [key, childValue] of Object.entries(value)) {
    sanitized[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? "[Filtered]"
      : sanitizeValue(childValue, depth + 1);
  }

  return sanitized;
}

export function scrubSentryEvent<T extends ScrubbableSentryEvent>(event: T): T {
  if (event.request) {
    event.request = sanitizeValue(event.request) as T["request"];
  }

  if (event.extra) {
    event.extra = sanitizeValue(event.extra) as T["extra"];
  }

  if (event.contexts) {
    event.contexts = sanitizeValue(event.contexts) as T["contexts"];
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
      if (!isRecord(breadcrumb)) return breadcrumb;
      return {
        ...breadcrumb,
        data: sanitizeValue(breadcrumb.data),
      };
    }) as T["breadcrumbs"];
  }

  return event;
}

export function getAxiosErrorMeta(error: unknown): SafeContext {
  if (!(error instanceof AxiosError)) return {};

  const data = error.response?.data;
  const body = isRecord(data) ? data : {};
  const code =
    typeof body.code === "string"
      ? body.code
      : typeof error.code === "string"
        ? error.code
        : undefined;

  return {
    status: error.response?.status ?? null,
    error_code: code ?? null,
    has_response: Boolean(error.response),
  };
}

export function shouldCaptureServiceError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return true;
  if (!error.response) return true;
  return (error.response.status ?? 0) >= 500;
}

export function captureAppError(
  error: unknown,
  { area, action, tags, extra }: CaptureAppErrorOptions,
): void {
  withScope((scope) => {
    scope.setTag("area", area);
    scope.setTag("action", action);

    for (const [key, value] of Object.entries(compactSafeContext(tags))) {
      scope.setTag(key, String(value));
    }

    scope.setContext("app", {
      area,
      action,
      ...compactSafeContext(extra),
      ...compactSafeContext(getAxiosErrorMeta(error)),
    });

    captureException(error);
  });
}

export function captureAppMessage(
  message: string,
  { area, action, tags, extra }: CaptureAppErrorOptions,
  level: "debug" | "info" | "warning" | "error" = "warning",
): void {
  withScope((scope) => {
    scope.setLevel(level);
    scope.setTag("area", area);
    scope.setTag("action", action);

    for (const [key, value] of Object.entries(compactSafeContext(tags))) {
      scope.setTag(key, String(value));
    }

    scope.setContext("app", {
      area,
      action,
      ...compactSafeContext(extra),
    });

    captureMessage(message);
  });
}

export function addAppBreadcrumb({
  category,
  message,
  level = "info",
  data,
}: AppBreadcrumbOptions): void {
  sentryAddBreadcrumb({
    category,
    message,
    level,
    data: compactSafeContext(data),
  });
}
